import PlainButton from "components/buttons/PlainButton";
import { range } from "sps-shared/dist/utils/array/range";
import styles from "components/buttons/css/BigToggleButton.module.css";
import BodyText from "components/text/BodyText";
import joinClasses from "utils/joinClasses";
import FontClass from "types/enums/FontClass";
import assertUnreachable from "sps-shared/dist/utils/assertUnreachable";
import ColorClass from "types/enums/ColorClass";
import { FixedLengthArray, IntRange } from "sps-shared/dist/types/UtilityTypes";

type ComponentSize = "normal" | "small";

function getToggleSizeClass(componentSize: ComponentSize) {
  switch (componentSize) {
    case "small":
      return {
        toggle: styles.toggleSmall,
        toggleHighlight: styles.toggleHighlightSmall,
      };
    case "normal":
      return {
        toggle: "",
        toggleHighlight: "",
      };
    default:
      return assertUnreachable(componentSize);
  }
}

type Props<T extends number> = {
  labels: FixedLengthArray<string | JSX.Element, T>;
  onToggle: (val: IntRange<0, T>) => void;
  position: IntRange<0, T>;
  size?: ComponentSize;
};

export default function BigToggleButton<T extends number>({
  labels,
  onToggle,
  position,
  size = "normal",
}: Props<T>): JSX.Element {
  const numToggles = labels.length;
  const toggleButtons = range(numToggles).map((val) => (
    <PlainButton
      key={val}
      className={styles.toggleButton}
      onClick={() => onToggle(val as IntRange<0, T>)}
    >
      <BodyText
        colorClass={null}
        className={position === val ? styles.textWhite : ColorClass.Primary}
        fontClass={size ? FontClass.Body1Medium : FontClass.NavLink}
      >
        {labels[val]}
      </BodyText>
    </PlainButton>
  ));

  return (
    <div
      className={joinClasses(styles.toggle, getToggleSizeClass(size).toggle)}
      style={{ gridTemplateColumns: `repeat(${numToggles}, minmax(0, 1fr))` }}
    >
      <div
        className={joinClasses(
          styles.toggleHighlight,
          getToggleSizeClass(size).toggleHighlight
        )}
        style={{
          left: `calc((7px * (2 * ${position} + 1) + ((${
            100 / numToggles
          }% - 14px) * ${position}))`,
          width: `calc(${100 / numToggles}% - 14px)`,
        }}
      />
      {toggleButtons}
    </div>
  );
}
